import { useEffect } from "react";

const FADE_MS = 300;

// ROW_GLOW_BLOCKS: aaneengesloten geselecteerde rijen krijgen samen één glow.
// Elke rij tekent in zijn eigen ::before het stuk van een gradient zo groot als
// het hele blok (background-size/-position), zodat het ook klopt als rijen
// buiten beeld niet gerenderd zijn. Losse rijen blijven zoals ze waren.
//
// Een net uitgevinkte rij faadt uit in de vorm die hij had; de rijen die
// geselecteerd blijven vormen meteen hun nieuwe blokken (CSS-transition, zie
// .MuiDataGrid-row::before in TransparentDataGrid.css). Wijzigingen worden per
// frame verzameld, zodat een blok dat in één keer wordt uitgevinkt (rij voor
// rij via de actiebalk) ook als geheel uitfadet.
// De regels gaan rechtstreeks in een eigen <style>, zonder React-render.
export default function useRowGlowBlocks(apiRef, enabled) {
    useEffect(() => {
        if (!enabled) return;
        const api = apiRef.current;
        if (!api) return;
        const style = document.createElement("style");
        document.head.appendChild(style);
        let rules = new Map(); // row id -> css-regel (alleen rijen in een blok)
        let frame = null;
        let pruneTimer = null;

        const apply = () => {
            const css = [...rules.values()].join("\n");
            if (style.textContent !== css) style.textContent = css;
        };

        const update = () => {
            frame = null;
            const selected = api.getSelectedRows();
            const next = new Map();
            const flush = (block) => {
                const n = block.length;
                if (n < 2) return;
                // Groter blok = fellere glow, anders smeert hij uit
                const alpha = Math.min(0.3 + 0.06 * (n - 1), 0.55).toFixed(2);
                block.forEach((id, k) => {
                    const sel = `.MuiDataGrid-row[data-id="${CSS.escape(String(id))}"]::before`;
                    next.set(id, `${sel}{background-size:100% ${n * 100}%,100% 100%;` +
                        `background-position:0 ${(k / (n - 1)) * 100}%,0 0;--glow-a:${alpha};}`);
                });
            };
            let block = [];
            for (const id of api.getSortedRowIds()) {
                if (selected.has(id)) block.push(id);
                else { flush(block); block = []; }
            }
            flush(block);
            // Net uitgevinkt (of nog aan het uitfaden): oude vorm houden
            for (const [id, rule] of rules) {
                if (!selected.has(id)) next.set(id, rule);
            }
            rules = next;
            apply();
            clearTimeout(pruneTimer);
            pruneTimer = setTimeout(() => {
                const sel = api.getSelectedRows();
                rules = new Map([...rules].filter(([id]) => sel.has(id)));
                apply();
            }, FADE_MS);
        };
        const schedule = () => {
            if (frame == null) frame = requestAnimationFrame(update);
        };

        update();
        const unsubs = ["rowSelectionChange", "sortedRowsSet"].map((e) => api.subscribeEvent(e, schedule));
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(pruneTimer);
            unsubs.forEach((u) => u());
            style.remove();
        };
    }, [apiRef, enabled]);
}
